import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import {
  TeamMemberProjectsById,
  ProjectsByTeamMember,
  TeamMemberProjects,
} from 'utils/types/dailyTeamsCapacity';
import { addIdsToArrayIfNotExists } from '../helpers';

const emptyTeamMember: TeamMemberProjects = {
  projectsIds: [],
  links: { next: null },
};

export const toggleMemberFromProjectsByTeamMember: (
  byTeamMember: ProjectsByTeamMember<TeamMemberProjects>,
  teamId: string,
  memberId: string
) => ProjectsByTeamMember<TeamMemberProjects> = (
  byTeamMember,
  teamId,
  memberId
) => {
  const updatedByTeamMember = cloneDeep(byTeamMember);
  if (!(teamId in updatedByTeamMember)) {
    set(updatedByTeamMember, teamId, {
      [memberId]: emptyTeamMember,
      allProjectsIds: [],
    });
  } else {
    if (memberId in updatedByTeamMember[teamId]) {
      delete updatedByTeamMember[teamId][memberId];
    } else {
      set(updatedByTeamMember, teamId, {
        ...updatedByTeamMember[teamId],
        [memberId]: emptyTeamMember,
      });
    }
  }
  if (Object.keys(updatedByTeamMember[teamId]).length === 1) {
    delete updatedByTeamMember[teamId];
  }
  return updatedByTeamMember;
};

export const removeTeamFromProjectsById: (
  projectsById: TeamMemberProjectsById,
  projectsIds: string[],
  teamMemberId: string
) => {
  projectsIdsToRemove: string[];
  updatedProjectsById: TeamMemberProjectsById;
} = (projectsById, projectsIds, teamMemberId) => {
  const projectsIdsToRemove = [];
  const updatedProjectsById = cloneDeep(projectsById);
  for (const projectId of projectsIds) {
    if (projectId in updatedProjectsById) {
      const teamMembersIds = updatedProjectsById[projectId].teamMembersIds;
      if (teamMembersIds.length === 1) {
        projectsIdsToRemove.push(projectId);
        delete updatedProjectsById[projectId];
      } else {
        updatedProjectsById[projectId] = {
          ...updatedProjectsById[projectId],
          teamMembersIds: teamMembersIds.filter((id) => id !== teamMemberId),
        };
      }
    }
  }
  return { projectsIdsToRemove, updatedProjectsById };
};

export const addProjectsToTeamMember: (
  byTeamMember: ProjectsByTeamMember<TeamMemberProjects>,
  teamId: string,
  memberId: string,
  projectsIds: string[],
  links: { next: string | null }
) => ProjectsByTeamMember<TeamMemberProjects> = (
  byTeamMember,
  teamId,
  memberId,
  projectsIds,
  links
) => {
  const updatedByTeamMember = cloneDeep(byTeamMember);
  const teamMemberProjects: TeamMemberProjects = {
    projectsIds: addIdsToArrayIfNotExists(
      projectsIds,
      updatedByTeamMember[teamId]?.[memberId]?.projectsIds || []
    ),
    links,
  };
  const allProjectsIds = addIdsToArrayIfNotExists(
    projectsIds,
    updatedByTeamMember[teamId]?.allProjectsIds || []
  );
  set(updatedByTeamMember, teamId, {
    ...updatedByTeamMember[teamId],
    [memberId]: teamMemberProjects,
    allProjectsIds,
  });
  return updatedByTeamMember;
};

export const replaceProjectsForTeamMember: (
  byTeamMember: ProjectsByTeamMember<TeamMemberProjects>,
  projectsById: TeamMemberProjectsById,
  teamId: string,
  memberId: string,
  newProjectsIds: string[],
  links: { next: string | null }
) => {
  updatedByTeamMember: ProjectsByTeamMember<TeamMemberProjects>;
  updatedProjectsById: TeamMemberProjectsById;
} = (byTeamMember, projectsById, teamId, memberId, newProjectsIds, links) => {
  const updatedByTeamMember = cloneDeep(byTeamMember);
  const oldProjectsIds =
    updatedByTeamMember[teamId]?.[memberId]?.projectsIds || [];

  const teamMemberId = `team-${teamId}__member-${memberId}`;
  const removedProjectIds = oldProjectsIds.filter(
    (id) => !newProjectsIds.includes(id)
  );

  let updatedProjectsById = { ...projectsById };

  // Clean up stale byId entries for removed projects
  for (const projectId of removedProjectIds) {
    if (projectId in updatedProjectsById) {
      const teamMembersIds = updatedProjectsById[projectId].teamMembersIds;
      if (teamMembersIds.length === 1 && teamMembersIds[0] === teamMemberId) {
        delete updatedProjectsById[projectId];
      } else {
        updatedProjectsById[projectId] = {
          ...updatedProjectsById[projectId],
          teamMembersIds: teamMembersIds.filter((id) => id !== teamMemberId),
        };
      }
    }
  }

  // Update allProjectsIds: remove projects no longer in any member
  const teamData = updatedByTeamMember[teamId];
  const remainingMembersProjectIds = Object.entries(teamData || {})
    .filter(([key]) => key !== 'allProjectsIds' && key !== memberId)
    .flatMap(([, member]) => (member as TeamMemberProjects).projectsIds);

  const allProjectsIds = [
    ...new Set([...remainingMembersProjectIds, ...newProjectsIds]),
  ];

  set(updatedByTeamMember, teamId, {
    ...updatedByTeamMember[teamId],
    [memberId]: {
      projectsIds: newProjectsIds,
      links,
    },
    allProjectsIds,
  });

  return { updatedByTeamMember, updatedProjectsById };
};
